import type { SimpleAccount } from '@/lib/types';

/** 表示文字列を検索語の一致箇所で 3 つに分けたもの（一致が無ければ全体が `before`）。 */
export interface TextMatch {
  before: string;
  match: string;
  after: string;
}

/** アカウント選択リストの 1 欄。 */
export interface AccountSection {
  heading: string;
  accounts: SimpleAccount[];
}

// 全角英数字で入力されても一致させるため NFKC で揃える。
const normalize = (value: string): string => value.normalize('NFKC').toLowerCase();

/** `text` のうち検索語に最初に一致した箇所を切り出す。 */
export function splitMatch(text: string, query: string): TextMatch {
  const normalizedQuery = normalize(query.trim());
  const normalizedText = normalize(text);
  const index = normalizedQuery ? normalizedText.indexOf(normalizedQuery) : -1;
  // 正規化で長さが変わる文字（半角カナの濁点など）を含むと位置がずれるため、その場合は強調しない。
  if (index < 0 || normalizedText.length !== text.length) {
    return { before: text, match: '', after: '' };
  }
  const end = index + normalizedQuery.length;
  return { before: text.slice(0, index), match: text.slice(index, end), after: text.slice(end) };
}

/** 一致の強さ（小さいほど上に出す）。一致しなければ null。 */
function matchRank(account: SimpleAccount, query: string): number | null {
  const code = normalize(account.code);
  if (code.startsWith(query)) return 0;
  if (code.includes(query)) return 1;
  if (normalize(account.name).includes(query)) return 2;
  return null;
}

/**
 * アカウント選択リストの欄を作る。検索語が無ければ「お気に入り」と「すべてのアカウント」に分け、
 * 検索語があれば一致したものを 1 欄にまとめ、一致の強さ → お気に入り → 元の順に並べる。
 */
export function groupAccountsForPicker(
  accounts: SimpleAccount[],
  query: string,
  favoriteAccountIds: string[],
): AccountSection[] {
  const favorites = new Set(favoriteAccountIds);
  const normalizedQuery = normalize(query.trim());

  if (!normalizedQuery) {
    return [
      { heading: 'お気に入り', accounts: accounts.filter((a) => favorites.has(a.id)) },
      { heading: 'すべてのアカウント', accounts: accounts.filter((a) => !favorites.has(a.id)) },
    ].filter((section) => section.accounts.length > 0);
  }

  const results = accounts
    .map((account, index) => ({ account, index, rank: matchRank(account, normalizedQuery) }))
    .filter((entry): entry is typeof entry & { rank: number } => entry.rank !== null)
    .sort(
      (a, b) =>
        a.rank - b.rank ||
        Number(favorites.has(b.account.id)) - Number(favorites.has(a.account.id)) ||
        a.index - b.index,
    )
    .map((entry) => entry.account);
  return results.length > 0 ? [{ heading: '検索結果', accounts: results }] : [];
}
